import React, { useEffect, useState } from 'react';
import { MessageSquareText, X } from 'lucide-react';
import { authHeaders } from '../utils/useLog';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/sg-lab`;
const API = `${BASE}/questions`;
const today = () => new Date().toLocaleDateString('en-CA');

const Q = ({ q, val, set }) => (
  <div className="border rounded-lg p-3 space-y-1.5 bg-white" data-testid={`lab-prompt-q-${q.id}`}>
    <div className="text-sm font-semibold text-slate-800">{q.title}</div>
    <div className="text-xs text-slate-600">{q.q}</div>
    <textarea className="w-full border rounded px-2 py-1 text-sm min-h-[52px]" value={val || ''} onChange={(e) => set(e.target.value)}
      placeholder="Optional - answer in your own words" data-testid={`lab-prompt-answer-${q.id}`} />
  </div>
);

const FreeRule = ({ val, set }) => (
  <div className="border-2 border-blue-300 rounded-lg p-3 space-y-1.5 bg-blue-50/40" data-testid="lab-prompt-free-rule">
    <div className="text-sm font-semibold text-slate-800">Add your own rule</div>
    <div className="text-xs text-slate-600">Tell ShaftGenius anything County always does - no need to wait for a question. One rule per line is fine.</div>
    <textarea className="w-full border rounded px-2 py-1 text-sm min-h-[64px] bg-white" value={val || ''} onChange={(e) => set(e.target.value)}
      placeholder='e.g. "We never put a lift joint within 18 in. of a door head"' data-testid="lab-prompt-free-rule-input" />
  </div>
);

const Thanks = ({ t }) => (
  <div className="rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-green-900" data-testid={`lab-prompt-thanks-${t.key}`}>
    <b>Thank you!</b> Your answer on <b>{t.title}</b> is now part of ShaftGenius ({t.rule}). It raised the match with County's own shop drawings from {t.before}% to <b>{t.after}%</b>.
  </div>
);

export const TeamNote = ({ t }) => (
  <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900" data-testid="lab-team-progress">
    {t.answers > 0
      ? <>County's answers have raised ShaftGenius's match with your shop drawings by <b>+{t.gain} points</b> ({t.from}% → <b>{t.now}%</b>) from {t.answers} approved answer{t.answers === 1 ? '' : 's'}: {t.rules.join(', ')}.</>
      : <>ShaftGenius currently matches County's own shop drawings <b>{t.now}%</b> of the time. Answer the lab questions when you sign in to raise it.</>}
  </div>
);

export const LabQuestionsPrompt = ({ email }) => {
  const key = `sglabq_${email}`;
  const [qs, setQs] = useState(null); const [thanks, setThanks] = useState([]); const [fus, setFus] = useState([]); const [team, setTeam] = useState(null); const [ans, setAns] = useState({}); const [busy, setBusy] = useState(false); const [done, setDone] = useState('');
  useEffect(() => {
    if (!email) return undefined;
    let got = null; let stop = false;
    const tryShow = () => {                                            // wait until the terms gate (if any) is accepted
      if (stop || !got || document.querySelector('[data-testid="terms-gate-modal"]')) return;
      stop = true;
      const fresh = localStorage.getItem(key) !== today();              // once a day: questions + the own-rule field
      const q = fresh ? (got.questions || []) : []; const th = got.thanks || []; const fu = got.followups || [];
      if (!fresh && !th.length && !fu.length) return;
      setQs(q); setThanks(th); setFus(fu); setTeam(got.team); if (fresh) localStorage.setItem(key, today());
      th.forEach((x) => fetch(`${BASE}/thanks/${x.key}/seen`, { method: 'POST', credentials: 'include', headers: authHeaders() }).catch(() => {}));
    };
    fetch(API, { credentials: 'include', headers: authHeaders() }).then((r) => (r.ok ? r.json() : null))
      .then((d) => { got = d || { questions: [] }; tryShow(); }).catch(() => {});
    const t = setInterval(tryShow, 2000);
    return () => { stop = true; clearInterval(t); };
  }, [email, key]);
  if (!qs) return null;
  const close = () => setQs(null);
  const filled = Object.entries(ans).filter(([, v]) => v && v.trim());
  const submit = async () => {
    setBusy(true); let ok = 0; let taken = 0;
    for (const [qid, text] of filled) {
      const fu = qid.startsWith('fu_');
      const url = qid === 'free_rule' ? `${BASE}/rules` : fu ? `${BASE}/followups/${qid.slice(3)}` : `${API}/${qid}`;
      const r = await fetch(url, { method: 'POST', credentials: 'include', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ text }) }).catch(() => null);
      if (r?.ok) ok += 1; else if (r?.status === 409) taken += 1;
    }
    const fail = filled.length - ok - taken;
    setDone(`Thank you - ${ok} answer${ok === 1 ? '' : 's'} sent to LightStream.${taken ? ` ${taken} had just been answered by a colleague.` : ''}${fail ? ` ${fail} could not be sent - please try again later.` : ''}`);
    setTimeout(close, fail ? 4000 : 2200);
  };
  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-3" data-testid="lab-questions-prompt">
      <div className="bg-slate-50 rounded-xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col">
        <div className="flex items-start gap-3 p-4 border-b bg-white rounded-t-xl">
          <MessageSquareText className="text-blue-700 mt-0.5" size={22} />
          <div className="flex-1">
            <div className="font-semibold text-slate-900">Help ShaftGenius draw shafts the County way</div>
            <div className="text-xs text-slate-600">ShaftGenius compares its designs with County's own shop drawings. A few answers about how County does things let it match you more closely. Answer any you like - every answer is optional, reviewed by LightStream before it changes anything.</div>
          </div>
          <button onClick={close} className="p-1 rounded hover:bg-slate-100" data-testid="lab-prompt-close" aria-label="Close"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto p-4 space-y-2">
          <FreeRule val={ans.free_rule} set={(v) => setAns((a) => ({ ...a, free_rule: v }))} />
          {team?.answers > 0 && <TeamNote t={team} />}
          {thanks.map((t) => <Thanks key={t.key} t={t} />)}
          {fus.map((f) => <Q key={`fu_${f.id}`} q={{ id: `fu_${f.id}`, title: `Follow-up: ${f.title}`, q: `You said: "${f.answer}". ${f.q}` }} val={ans[`fu_${f.id}`]} set={(v) => setAns((a) => ({ ...a, [`fu_${f.id}`]: v }))} />)}
          {qs.map((q) => <Q key={q.id} q={q} val={ans[q.id]} set={(v) => setAns((a) => ({ ...a, [q.id]: v }))} />)}
        </div>
        <div className="flex items-center gap-2 p-3 border-t bg-white rounded-b-xl">
          {done ? <span className="text-sm text-green-700" data-testid="lab-prompt-done">{done}</span> : qs.length ? <span className="text-xs text-slate-500">We'll ask again tomorrow if you choose Later.</span> : null}
          <button onClick={close} className="ml-auto px-3 py-1.5 rounded border text-sm" data-testid="lab-prompt-later">{qs.length ? 'Later' : 'Close'}</button>
          {<button onClick={submit} disabled={!filled.length || busy || !!done} className="px-3 py-1.5 rounded bg-blue-700 text-white text-sm disabled:opacity-50" data-testid="lab-prompt-submit">
            {busy ? 'Sending…' : `Send ${filled.length || ''} answer${filled.length === 1 ? '' : 's'}`}</button>}
        </div>
      </div>
    </div>
  );
};
